import { Injectable } from '@angular/core';
import { AwsRum, AwsRumConfig } from 'aws-rum-web';

export interface RumConfig {
  appId: string;
  identityPoolId: string;
  region: string;
  appVersion?: string;
  sessionSampleRate?: number;
  telemetries?: Array<'http' | 'errors' | 'performance'>;
  allowCookies?: boolean;
}

@Injectable({ providedIn: 'root' })
export class Rum {
  private rum?: AwsRum;

  init(cfg: RumConfig) {
    const config: AwsRumConfig = {
      identityPoolId: cfg.identityPoolId,
      sessionSampleRate: cfg.sessionSampleRate ?? 1,
      telemetries: cfg.telemetries ?? ['http', 'errors', 'performance'],
      allowCookies: cfg.allowCookies ?? true,
    };
    this.rum = new AwsRum(cfg.appId, cfg.appVersion ?? 'web', cfg.region, config);
  }

  get instance() {
    return this.rum;
  }

  addSessionAttributes(attrs?: Record<string, string | number | boolean>) {
    if (!this.rum || !attrs) return;
    if (Object.keys(attrs).length === 0) return;
    this.rum.addSessionAttributes(attrs);
  }

  recordEvent(type: string, attrs?: Record<string, unknown>) {
    if (!this.rum) return;

    const payload: object = attrs && Object.keys(attrs).length > 0 ? (attrs as object) : {};
    this.rum.recordEvent(type, payload);
  }

  recordError(err: unknown) {
    const e = err instanceof Error ? err : new Error(String(err));
    this.rum?.recordError?.(e);
  }

  /** Attach Cognito identity */
  setIdentity(opts: { actor?: string; sub?: string; email?: string; name?: string }) {
    const attrs: Record<string, string> = {};
    if (opts.actor) attrs['actor'] = opts.actor;
    if (opts.sub) attrs['cognitoSub'] = opts.sub;
    if (opts.email) attrs['email'] = opts.email;
    if (opts.name) attrs['name'] = opts.name;
    this.addSessionAttributes(attrs);
  }

  /** Detach Cognito identity */
  clearIdentity() {
    this.addSessionAttributes({
      actor: 'anonymous',
      cognitoSub: 'anonymous',
      email: '',
      name: '',
      loggedOut: 'true',
    });
  }
}
